import { RosterHostComponent } from './roster-host-component';
import { computed, effect, inject, linkedSignal, ResourceStatus } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RosterDepartmentHeadService } from '../services/roster-department-head.service';
import { DepartmentHead } from '../types/department-head';

export interface DepartmentHeadDisplay extends DepartmentHead {
  costCenters: string;
  department: string;
}

/**
 * The main component that utilizes the feature registry to create and manage
 * the requested feature with the provided RosterFeature-Configuration and it's deps
 * All components provided in a RosterFeature configuration can inject this host
 */
export abstract class RosterWithDepartmentHeadHostComponent extends RosterHostComponent {
  readonly departmentHeadService = inject(RosterDepartmentHeadService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private departmentHeadInitialized = false;

  protected readonly departmentHeads = linkedSignal<
    { value: DepartmentHead[]; status: ResourceStatus },
    DepartmentHead[]
  >({
    source: () => {
      return {
        status: this.departmentHeadService.status(),
        value: this.departmentHeadService.all(),
      };
    },
    computation: (source, previous) => {
      if (source.status === 'loading' || source.status === 'reloading')
        return previous?.value ?? source.value;

      return source?.value ?? [];
    },
  });

  readonly _departmentHeads = computed<DepartmentHeadDisplay[]>(() => {
    const depHeads = this.departmentHeads() ?? [];
    return depHeads.map((dh) => ({
      ...dh,
      costCenters:
        dh.managedDepartments
          ?.map((d) => d.costCenters.map((c) => c.key).join(', '))
          .join(', ') ?? '',
      department: dh.managedDepartments?.map((d) => d.name).join(', ') ?? '',
    }));
  });

  readonly departmentHead = linkedSignal<DepartmentHeadDisplay[], DepartmentHead | null>({
    source: this._departmentHeads,
    computation: (heads, previous) => {
      if (heads.length === 0) return null;

      // On first load, restore from query param or localStorage
      if (!this.departmentHeadInitialized) {
        this.departmentHeadInitialized = true;
        const queryId = this.route.snapshot.queryParamMap.get('dept');
        const storedId =
          queryId ?? localStorage.getItem('roster_selected_department_head_id');
        if (storedId) {
          const match = heads.find((h) => h.employee.id === Number(storedId));
          if (match) return match;
        }
      }

      // Keep current selection if it still exists in the new list
      if (previous?.value) {
        const current = heads.find((h) => h.employee.id === previous.value?.employee.id);
        if (current) return current;
      }

      return heads[0];
    },
  });

  constructor() {
    super();

    // Sync query param when department head changes
    effect(() => {
      const dh = this.departmentHead();
      if (!dh) return;
      const id = String(dh.employee.id);
      const currentParam = this.route.snapshot.queryParamMap.get('dept');
      if (currentParam !== id) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { dept: id },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
    });
  }

  override onDepartmentHeadChange = async (employee: DepartmentHead) => {
    localStorage.setItem(
      'roster_selected_department_head_id',
      String(employee.employee.id)
    );
    this.departmentHead.set(employee);
  };
}
